// Reusable user / group pickers. Both query the API once and cache via React Query.
// Single-select and multi-select variants share a small primitive.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiList } from '../lib/api.js';
import type { Group, User } from '@captain-tasko/shared';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiList<User>('/users'),
    staleTime: 60_000,
  });
}

export function useGroups() {
  return useQuery({
    queryKey: ['groups'],
    queryFn: () => apiList<Group>('/groups'),
    staleTime: 60_000,
  });
}

/** Renders a list of user IDs as display-name chips (with avatar/initials).  */
export function UserChips({ ids, fallback = '—' }: { ids: string[]; fallback?: string }) {
  const users = useUsers();
  const byId = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users.data?.data ?? []) m.set(u.id, u);
    return m;
  }, [users.data]);

  if (ids.length === 0) return <span style={{ color: 'var(--text-muted)' }}>{fallback}</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {ids.map((id) => {
        const u = byId.get(id);
        return <UserChip key={id} user={u ?? null} fallbackId={id} />;
      })}
    </span>
  );
}

export function UserChip({ user, fallbackId }: { user: User | null; fallbackId?: string }) {
  const label = user?.displayName ?? fallbackId ?? '?';
  const initials = (label[0] ?? '?').toUpperCase();
  return (
    <span
      title={user ? `${user.displayName} <${user.email}>` : `Unknown user ${fallbackId}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px 2px 2px',
        background: 'var(--surface-sunken)',
        borderRadius: 999,
        fontSize: 12,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: 'var(--accent-primary)',
          color: '#fff',
          display: 'inline-grid',
          placeItems: 'center',
          fontSize: 10,
          fontWeight: 600,
        }}
      >
        {initials}
      </span>
      {label}
    </span>
  );
}

/** Single user picker — null = unassigned. */
export function UserSelect({
  value,
  onChange,
  placeholder = '— None —',
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
}) {
  const users = useUsers();
  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      style={{ width: '100%', padding: 4, fontSize: 13 }}
    >
      <option value="">{placeholder}</option>
      {(users.data?.data ?? []).map((u) => (
        <option key={u.id} value={u.id}>{u.displayName}</option>
      ))}
    </select>
  );
}

/** Multi-select for users — renders as checkboxes inside a bordered scroll panel. */
export function UserMultiSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const users = useUsers();
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 4, padding: 6, maxHeight: 160, overflow: 'auto' }}>
      {users.isLoading && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</span>}
      {(users.data?.data ?? []).map((u) => {
        const checked = selected.includes(u.id);
        return (
          <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '2px 0' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) =>
                onChange(e.target.checked ? [...selected, u.id] : selected.filter((id) => id !== u.id))
              }
            />
            <UserChip user={u} />
          </label>
        );
      })}
    </div>
  );
}

/** Multi-select for groups (used as teams in the work-item context). */
export function GroupMultiSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const groupsQ = useGroups();
  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 4, padding: 6, maxHeight: 120, overflow: 'auto' }}>
      {groupsQ.isLoading && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Loading…</span>}
      {(groupsQ.data?.data ?? []).map((g) => {
        const checked = selected.includes(g.id);
        return (
          <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '2px 0' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) =>
                onChange(e.target.checked ? [...selected, g.id] : selected.filter((id) => id !== g.id))
              }
            />
            <strong>{g.name}</strong>
            <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {g.memberCount} members</span>
          </label>
        );
      })}
    </div>
  );
}
